import React, { useEffect, useRef, useState } from 'react';
import {
    IonModal, IonHeader, IonToolbar, IonTitle, IonButtons, IonButton,
    IonContent, IonFooter, IonIcon, IonText, IonSpinner, IonLabel, IonAlert,
} from '@ionic/react';
import { closeOutline, trashOutline, cloudUploadOutline } from 'ionicons/icons';
import { apiService } from '@/services/ApiService';
import Toaster, { ToasterVariant } from '@/components/common/Toaster';
import { LoginImageResponse } from '@/types/response/enterpriseResponses';

interface EnterpriseImageModalProps {
    isOpen: boolean;
    onDidDismiss: () => void;
    images: LoginImageResponse[];
    canEditAttachments: boolean;
    onSaved: () => void;
}

const EnterpriseImageModal: React.FC<EnterpriseImageModalProps> = ({
    isOpen, onDidDismiss, images, canEditAttachments, onSaved,
}) => {
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [overwrite, setOverwrite] = useState(false);
    const [selectedImage, setSelectedImage] = useState<string>('');
    const [isSaving, setIsSaving] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [toast, setToast] = useState<{ message: string; variant: ToasterVariant } | null>(null);
    const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (isOpen) {
            setSelectedFile(null);
            setOverwrite(false);
            setSelectedImage(images.find(i => i.IsSelected)?.FileName ?? '');
        }
    }, [isOpen, images]);

    const handleUpload = async () => {
        if (!selectedFile) { setToast({ message: 'Please select a file to upload.', variant: 'error' }); return; }
        const duplicate = images.some(i => i.FileName === selectedFile.name);
        if (duplicate && !overwrite) { setToast({ message: 'A file with this name already exists. Check "Overwrite" to replace it.', variant: 'error' }); return; }
        setIsUploading(true);
        try {
            const fd = new FormData();
            fd.append('file', selectedFile);
            await apiService.uploadLoginImage(fd);
            setSelectedFile(null);
            if (fileInputRef.current) fileInputRef.current.value = '';
            setToast({ message: 'Image uploaded.', variant: 'success' });
            onSaved();
        } catch {
            setToast({ message: 'Upload failed.', variant: 'error' });
        } finally {
            setIsUploading(false);
        }
    };

    const handleDelete = async (filename: string) => {
        setIsSaving(true);
        try {
            await apiService.deleteLoginImage({ filename });
            if (selectedImage === filename) setSelectedImage('');
            setToast({ message: 'Image deleted.', variant: 'success' });
            onSaved();
        } catch {
            setToast({ message: 'Delete failed.', variant: 'error' });
        } finally {
            setIsSaving(false);
            setConfirmDelete(null);
        }
    };

    const handleSaveSelection = async () => {
        if (!selectedImage) { setToast({ message: 'Please select an image.', variant: 'error' }); return; }
        setIsSaving(true);
        try {
            await apiService.saveLoginImageSelection({ LoginPageImageTop: selectedImage });
            setToast({ message: 'Image selection saved.', variant: 'success' });
            onSaved();
        } catch {
            setToast({ message: 'Failed to save selection.', variant: 'error' });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
            <IonModal isOpen={isOpen} onDidDismiss={onDidDismiss}
                style={{ '--width': '560px', '--height': '500px', '--border-radius': '8px' }}>
                <IonHeader>
                    <IonToolbar>
                        <IonTitle style={{ fontSize: '1rem' }}>Enterprise Image</IonTitle>
                        <IonButtons slot="end">
                            <IonButton onClick={onDidDismiss}><IonIcon icon={closeOutline} /></IonButton>
                        </IonButtons>
                    </IonToolbar>
                </IonHeader>

                <IonContent style={{ '--padding-start': '1.25rem', '--padding-end': '1.25rem', '--padding-top': '1rem' }}>
                    {(isSaving || isUploading) ? (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}><IonSpinner /></div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                            {/* Upload */}
                            {canEditAttachments && (
                                <div style={{ background: 'var(--ion-color-light)', borderRadius: '8px', padding: '0.75rem' }}>
                                    <IonLabel style={{ fontSize: '0.85rem', color: 'var(--ion-color-medium)', display: 'block', marginBottom: '0.5rem' }}>Upload New Image</IonLabel>
                                    <input ref={fileInputRef} type="file" accept="image/*"
                                        onChange={e => setSelectedFile(e.target.files?.[0] ?? null)}
                                        style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem' }} />
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                                        <input type="checkbox" id="overwrite" checked={overwrite} onChange={e => setOverwrite(e.target.checked)} />
                                        <label htmlFor="overwrite" style={{ fontSize: '0.85rem' }}>Overwrite if exists</label>
                                    </div>
                                    <IonButton size="small" fill="outline" color="primary" disabled={!selectedFile} onClick={handleUpload}>
                                        <IonIcon icon={cloudUploadOutline} slot="start" />Upload
                                    </IonButton>
                                </div>
                            )}

                            {/* Image list */}
                            <div>
                                <IonLabel style={{ fontSize: '0.85rem', color: 'var(--ion-color-medium)', display: 'block', marginBottom: '0.5rem' }}>
                                    Available Images {images.length > 0 && `(${images.length})`}
                                </IonLabel>
                                {images.length === 0 ? (
                                    <IonText color="medium" style={{ fontSize: '0.9rem' }}>No images available.</IonText>
                                ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                                        {images.map(img => (
                                            <div key={img.FileName} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.4rem 0', borderBottom: '1px solid var(--ion-color-light-shade)' }}>
                                                <input type="radio" name="image-select" value={img.FileName}
                                                    checked={selectedImage === img.FileName}
                                                    onChange={() => setSelectedImage(img.FileName)} />
                                                <IonText style={{ flex: 1, fontSize: '0.9rem' }}>{img.FileName}</IonText>
                                                {canEditAttachments && (
                                                    <IonButton fill="clear" size="small" color="danger" title="Delete"
                                                        onClick={() => setConfirmDelete(img.FileName)}>
                                                        <IonIcon icon={trashOutline} />
                                                    </IonButton>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </IonContent>

                {!isSaving && !isUploading && images.length > 0 && (
                    <IonFooter>
                        <IonToolbar style={{ '--padding-start': '1rem', '--padding-end': '1rem' }}>
                            <IonButtons slot="start">
                                <IonButton color="medium" onClick={onDidDismiss}>Close</IonButton>
                            </IonButtons>
                            <IonButtons slot="end">
                                <IonButton fill="solid" color="primary" disabled={!selectedImage} onClick={handleSaveSelection}>
                                    Set as Login Image
                                </IonButton>
                            </IonButtons>
                        </IonToolbar>
                    </IonFooter>
                )}
            </IonModal>

            <IonAlert isOpen={!!confirmDelete} header="Delete Image"
                message={`Delete "${confirmDelete}"? This cannot be undone.`}
                buttons={[
                    { text: 'Cancel', role: 'cancel', handler: () => setConfirmDelete(null) },
                    { text: 'Delete', role: 'destructive', handler: () => { if (confirmDelete) handleDelete(confirmDelete); } },
                ]}
                onDidDismiss={() => setConfirmDelete(null)}
            />
            <Toaster isOpen={!!toast} message={toast?.message} variant={toast?.variant} onDidDismiss={() => setToast(null)} />
        </>
    );
};

export default EnterpriseImageModal;
